import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { LessonBlockCard } from './LessonBlockCard';
import { useLessonBuilderStore } from '../../stores/lessonBuilderStore';

export const LessonCanvas: React.FC = () => {
  const pages = useLessonBuilderStore((s) => s.pages);
  const selectedPageId = useLessonBuilderStore((s) => s.selectedPageId);
  const moveBlock = useLessonBuilderStore((s) => s.moveBlock);
  const selectPage = useLessonBuilderStore((s) => s.selectPage);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const selectedPage = pages.find((p) => p.id === selectedPageId) || pages[0];
  const blocks = selectedPage?.blocks || [];

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;

    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      moveBlock(selectedPage?.id || '', oldIndex, newIndex);
    }
  };

  const pageIndex = selectedPage ? pages.findIndex((p) => p.id === selectedPage.id) : -1;
  const pageNumber = pageIndex >= 0 ? pageIndex + 1 : 0;
  const hasPrev = pageIndex > 0;
  const hasNext = pageIndex >= 0 && pageIndex < pages.length - 1;

  return (
    <div className="flex-1 flex justify-center">
      <div className="w-[420px] max-w-full">
        <div className="mx-auto mb-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Mobile preview</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Page {pageNumber} of {pages.length} • Tap blocks to edit
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                hasPrev ? 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-100' : 'bg-gray-100 text-gray-400'
              }`}
              onClick={() => hasPrev && selectPage(pages[pageIndex - 1].id)}
              disabled={!hasPrev}
            >
              Prev
            </button>
            <button
              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                hasNext ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'
              }`}
              onClick={() => hasNext && selectPage(pages[pageIndex + 1].id)}
              disabled={!hasNext}
            >
              Next
            </button>
          </div>
        </div>

        <div className="relative rounded-[2.5rem] border-8 border-gray-900 bg-gray-100 dark:bg-gray-950 shadow-xl overflow-hidden">
          <div className="absolute inset-0 pointer-events-none border-[14px] border-gray-900 rounded-[2rem]" />
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-2 bg-gray-900 rounded-full" />
          <div className="relative h-[720px] overflow-y-auto bg-white dark:bg-gray-900 px-4 py-5 space-y-3">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={blocks.map((b) => b.id)}
                strategy={verticalListSortingStrategy}
              >
                {(!selectedPage || blocks.length === 0) && (
                  <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-12">
                    Add blocks to this page to start composing.
                  </div>
                )}
                {selectedPage &&
                  blocks
                    .slice()
                    .sort((a, b) => a.position - b.position)
                    .map((block) => (
                      <LessonBlockCard key={block.id} block={block} pageId={selectedPage.id} />
                    ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>
    </div>
  );
};
